import { Header, Icon, Segment } from '@mairistem/composants';
import { Sens } from '@mairistem/comptabilite';
import { getErrorMessage } from '@mairistem/finances-utils';
import { omit } from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';
import PROJET_ROW_TYPE from '../../constants/common/projetRowType';
import STORAGE_KEY from '../../constants/common/storage';
import type CreditFilterBar from '../../interfaces/common/CreditFilterBar';
import type Projet from '../../interfaces/common/projet';
import { getProjets } from '../../services/projet';
import CreditPaiementTreeView from './CreditPaiementTreeView';
import CreditPaiementTreeViewFooter from './CreditPaiementTreeViewFooter';
import FilterBar from './FilterBar';

const anneeExercice = new Date().getFullYear();

const formatProjetToTreeView = (projet: Projet) => ({
	...omit(projet, ['composants']),
	children:
		projet.composants?.length > 0
			? projet.composants.map((composant) => ({
					...composant,
					rowKey: `${projet.identifiant.toString()}-${composant.identifiant.toString()}`,
					type: PROJET_ROW_TYPE.COMPOSANT,
				}))
			: [],
	rowKey: projet.identifiant.toString(),
	type: PROJET_ROW_TYPE.PROJET,
});

const CreditPaiement = () => {
	const [loading, setLoading] = React.useState(true);
	const [data, setData] = React.useState([]);
	const [metadata, setMetadata] = React.useState(null);
	const [filters, setFilters] = React.useState<CreditFilterBar>({
		decision: null,
		exercice: anneeExercice.toString(),
	});
	const identifiantEntiteCompta = sessionStorage.getItem(
		STORAGE_KEY.ID_ENTITE_CPTA,
	);

	const onRefresh = () => {
		fetchData(1, filters);
	};

	const fetchData = async (
		page: number = 1,
		newFilters: CreditFilterBar = null,
	) => {
		try {
			setLoading(true);
			const result = await getProjets(
				new URLSearchParams({
					...(newFilters.decision && {
						decision: newFilters.decision.toString(),
					}),
					exercice: newFilters.exercice,
					identifiantEntiteCompta: identifiantEntiteCompta.toString(),
					page: page.toString(),
					perPage: '15',
					sens: Sens.DEPENSE,
					withComposants: 'true',
					withFinancements: 'true',
					withTotaux: 'true',
				}),
			);

			setData(result.data.map(formatProjetToTreeView));
			setMetadata(result.meta);
		} catch (error) {
			toast.error(
				getErrorMessage(error, 'Erreur lors du chargement des projets.'),
			);
		} finally {
			setLoading(false);
		}
	};

	const handleFilterChange = (filter) => {
		setData([]);
		setMetadata(null);
		setFilters((old) => {
			const newFilters = { ...old, ...filter };
			fetchData(1, newFilters);
			return newFilters;
		});
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: Pas besoin de 'fetchData'
	React.useEffect(() => {
		fetchData(1, filters);
	}, []);

	return (
		<Segment basic>
			<Segment basic className="header-credit">
				<Header>
					<Icon color="var(--accent)" iconSet="Lucide" name="CreditCard" />
					<Header.Content>
						Crédits de paiement
						<Header.Subheader content="Gestion des crédits de paiement" />
					</Header.Content>
				</Header>

				<div>
					<FilterBar filters={filters} onFilterChange={handleFilterChange} />
				</div>
			</Segment>
			<Segment>
				<CreditPaiementTreeView
					data={data}
					metadata={metadata}
					onLoadData={fetchData}
					onRefresh={onRefresh}
					filters={filters}
					loading={loading}
				/>

				<CreditPaiementTreeViewFooter
					identifiantEntiteCompta={identifiantEntiteCompta}
					exercice={filters?.exercice}
					decision={filters?.decision}
				/>
			</Segment>
		</Segment>
	);
};

export default CreditPaiement;
